import {
  Badge,
  Flex,
  FormatDeliveryDate,
  IconButton,
  Text,
  Title,
} from '@wyz/ui';
import { DateUtils, invariant, MoneyUtils, ORDER_TYPE_CODE } from '@wyz/utils';
import { isNil, pick } from 'lodash-es';
import { useFormContext } from 'react-hook-form';
import { Trans, useLocale } from '~/common/context';
import { IUserCustomer } from '~/common/types';
import { useManageQuoteCart } from '~/purchase/api';
import {
  PurchaseOfferDescriptionCell,
  QuoteRowProductInfo,
  QuoteTable,
} from '~/purchase/components';
import { useQuoteCart } from '~/purchase/hooks';
import {
  IQuoteAddToCartForm,
  IQuoteItemInfo,
  IQuoteStock,
  IQuoteTyreOffer,
} from '~/purchase/types';

type Props = {
  tyreOffer: IQuoteTyreOffer;
  quoteItemInfo: IQuoteItemInfo;
  pointOfSaleView: boolean;
  onShowDetails: () => void;
  addItemLoading: boolean;
  customer?: IUserCustomer;
};

function QuoteRowItem({
  tyreOffer,
  quoteItemInfo,
  pointOfSaleView,
  onShowDetails,
  addItemLoading,
  customer,
}: Props) {
  const locale = useLocale();

  let stocks: Array<IQuoteStock | undefined> = pointOfSaleView
    ? (quoteItemInfo.stocks ?? [])
    : [];
  // at least one element to display tyre
  if (stocks.length === 0) {
    stocks = [undefined];
  }
  const rowSpan = stocks.length;
  const { actions } = useQuoteCart();
  const manageCart = useManageQuoteCart({
    get: { enabled: true },
  });
  const cartItem = manageCart.get.data?.data.items.find(
    (cartItem) => cartItem.tyre.ean === quoteItemInfo.tyre.ean,
  );

  const methods = useFormContext<IQuoteAddToCartForm>();
  const watchedQuantity = methods.watch('quantity');

  return (
    <QuoteTable pointOfSaleView={pointOfSaleView}>
      <tbody>
        {stocks.map((stock, stockIndex) => {
          const showCommon = stockIndex === 0;
          const hasStock = stock !== undefined;
          const backOrder = pointOfSaleView
            ? stock?.backOrder
            : tyreOffer.quoteSelectedOffer?.backOrder;
          const deliveryDate = pointOfSaleView
            ? stock?.deliveryDate
            : tyreOffer.quoteSelectedOffer?.deliveryDate;
          return (
            <tr key={stockIndex}>
              {showCommon && (
                <>
                  <td
                    id='product'
                    rowSpan={rowSpan}
                    className='align-middle text-truncate'
                  >
                    <QuoteRowProductInfo
                      tyreOffer={tyreOffer}
                      tyre={quoteItemInfo.tyre}
                      tyreIdentifierFields={
                        customer?.settingTemplate?.generalSetting
                          .tyreIdentifierFields ?? undefined
                      }
                      pointOfSaleView={pointOfSaleView}
                    />
                  </td>
                  <td id='description' rowSpan={rowSpan}>
                    <PurchaseOfferDescriptionCell
                      product={tyreOffer}
                      onDetail={onShowDetails}
                    />
                  </td>
                  <td
                    id='bf'
                    rowSpan={rowSpan}
                    className='text-center align-middle'
                  >
                    <Text heading='xs' weight='semi-bold' id='bf-price-value'>
                      {MoneyUtils.formatPrice(tyreOffer.tyre.priceFrame.price, {
                        currency: tyreOffer.tyre.priceFrame.currencyIsoCode,
                        locale: locale.locale,
                        fallback: '/',
                      })}
                    </Text>
                  </td>
                </>
              )}
              {pointOfSaleView && (
                <td id='supplier' className='text-center align-middle'>
                  <Badge bg='info' pill>
                    <Text weight='semi-bold' color='inherit' heading='xs'>
                      {hasStock ? (
                        stock.supplierName
                      ) : (
                        <Trans i18nKey='app.purchase.quote_search_page.product.availability.unavailable' />
                      )}
                    </Text>
                  </Badge>
                </td>
              )}
              <td id='delivery' className='text-center align-middle'>
                <Text heading='sm' weight='semi-bold'>
                  <FormatDeliveryDate
                    orderType={backOrder ? ORDER_TYPE_CODE.BO : null}
                    backorderContent={
                      <Trans i18nKey='app.common.offer_type.backorder' />
                    }
                    date={deliveryDate}
                    format={DateUtils.DATE_FORMAT.DM}
                    locale={locale.locale}
                    fallback='/'
                  />
                </Text>
              </td>
              {pointOfSaleView && (
                <>
                  <td id='stock' className='text-center align-middle'>
                    <Text heading='sm' weight='semi-bold'>
                      {hasStock ? stock.quantity : '/'}
                    </Text>
                  </td>
                  <td id='margin' className='text-center align-middle'>
                    <Text heading='sm' weight='semi-bold'>
                      {MoneyUtils.formatPrice(
                        quoteItemInfo.pricing?.unitPriceTaxIncl,
                        {
                          currency: tyreOffer.tyre.priceFrame.currencyIsoCode,
                          locale: locale.locale,
                          fallback: '/',
                        },
                      )}
                    </Text>
                    <Text heading='sm' weight='semi-bold' color='info'>
                      {MoneyUtils.formatPrice(stock?.margin, {
                        currency: tyreOffer.tyre.priceFrame.currencyIsoCode,
                        locale: locale.locale,
                        fallback: '/',
                      })}
                    </Text>
                  </td>
                </>
              )}
              {showCommon && (
                <>
                  <td
                    id='selection'
                    rowSpan={rowSpan}
                    className='text-center align-middle'
                  >
                    {pointOfSaleView ? (
                      <Flex
                        direction='column'
                        justifyContent='around'
                        alignItems='center'
                        wrap='wrap'
                      >
                        <Badge bg='info' pill>
                          <Text
                            weight='semi-bold'
                            color='inherit'
                            heading='sm'
                            className='capitalize'
                          >
                            <Trans
                              i18nKey='app.quote.tyre_count'
                              values={{ count: watchedQuantity }}
                            />
                          </Text>
                        </Badge>
                        <Title weight='semi-bold' heading='lg'>
                          <Trans
                            i18nKey='app.purchase.quote_search_page.product.price_with_taxes'
                            values={{
                              price: !isNil(
                                quoteItemInfo.pricing?.unitPriceTaxIncl,
                              )
                                ? MoneyUtils.formatPrice(
                                    watchedQuantity *
                                      quoteItemInfo.pricing.unitPriceTaxIncl,
                                    {
                                      currency:
                                        tyreOffer.tyre.priceFrame
                                          .currencyIsoCode,
                                      locale: locale.locale,
                                      fallback: '/',
                                    },
                                  )
                                : '-',
                            }}
                          />
                        </Title>
                      </Flex>
                    ) : (
                      <div>
                        <Badge bg='info' pill>
                          <Text
                            weight='semi-bold'
                            color='inherit'
                            heading='sm'
                            className='capitalize'
                          >
                            <Trans
                              i18nKey='app.quote.tyre_count'
                              values={{ count: watchedQuantity }}
                            />
                          </Text>
                        </Badge>
                        <Title weight='semi-bold' heading='lg'>
                          <Trans
                            i18nKey='app.purchase.quote_search_page.product.price_with_taxes'
                            values={{
                              price: !isNil(
                                quoteItemInfo.pricing?.unitPriceTaxIncl,
                              )
                                ? MoneyUtils.formatPrice(
                                    watchedQuantity *
                                      quoteItemInfo.pricing.unitPriceTaxIncl,
                                    {
                                      currency:
                                        tyreOffer.tyre.priceFrame
                                          .currencyIsoCode,
                                      locale: locale.locale,
                                      fallback: '/',
                                    },
                                  )
                                : '-',
                            }}
                          />
                        </Title>
                        <Text>
                          <Trans i18nKey='app.purchase.quote_cart_page.total_section.unit_price' />
                          <span className='ms-1'>
                            {MoneyUtils.formatPrice(
                              quoteItemInfo.pricing?.unitPriceTaxIncl,
                              {
                                currency:
                                  tyreOffer.tyre.priceFrame.currencyIsoCode,
                                locale: locale.locale,
                                fallback: '/',
                              },
                            )}
                          </span>
                        </Text>
                      </div>
                    )}
                  </td>
                  <td
                    id='action'
                    rowSpan={rowSpan}
                    className='text-center align-middle'
                  >
                    <Flex
                      direction='column'
                      justifyContent='center'
                      alignItems='center'
                      className='m-2'
                    >
                      <IconButton
                        id={`add-to-quote-${quoteItemInfo.tyre.ean}`}
                        icon='shopping-cart'
                        iconAlign='right'
                        type='submit'
                        variant='primary'
                        size='sm'
                        isLoading={addItemLoading}
                        disabled={!isNil(cartItem)}
                      >
                        <Trans i18nKey='app.common.button.add' />
                      </IconButton>
                      {!isNil(cartItem) && (
                        <IconButton
                          id={`remove-from-cart-${quoteItemInfo.tyre.ean}`}
                          icon='trash'
                          iconSize='sm'
                          iconAlign='right'
                          variant='tertiary-grey'
                          onClick={() => {
                            invariant(quoteItemInfo.pricing);
                            const payload = {
                              ...pick(quoteItemInfo, ['tyre', 'stocks']),
                              buyingPrice: quoteItemInfo.pricing.buyingPrice,
                              unitPriceTaxIncl:
                                quoteItemInfo.pricing.unitPriceTaxIncl,
                              recyclingFee: quoteItemInfo.pricing.recyclingFee,
                              quantity: watchedQuantity,
                              currency:
                                tyreOffer.tyre.priceFrame.currencyIsoCode,
                            };
                            actions.removeItem.process(cartItem.id, payload);
                          }}
                          isLoading={actions.removeItem.isLoading}
                        >
                          <Trans i18nKey='app.purchase.quote_search_page.product.delete' />
                        </IconButton>
                      )}
                    </Flex>
                  </td>
                </>
              )}
            </tr>
          );
        })}
      </tbody>
    </QuoteTable>
  );
}

export default QuoteRowItem;
